/// <amd-module name="TFS/CreateProject/Store" />
import * as Utils_String from "VSS/Core/Util/String";
import * as VSSStore from "VSS/Reflux/Store";

import {
    IProjectCreationMetadataLoadedPayload,
    IProjectCreationStatusPayload,
    IProjectLimitCheckStatusPayload,
    IProjectNameValidationStatusPayload
} from "TFS/CreateProject/ActionsHub";
import {
    IChildOptionItem,
    IOptionItem,
    IProcessTemplateDescriptor,
    IProjectCreationMetadataItemDescriptor,
    StatusMessageType,
    StatusType,
    StatusValueType
} from "TFS/CreateProject/Contracts";
import { ICreateProjectComponentState } from "TFS/CreateProject/Models";
import * as CreateProjectResources from "TFS/CreateProject/Resources";

export class Store extends VSSStore.Store {
    private _state: ICreateProjectComponentState;

    constructor(
        private _source: string,
        private _versionControlType?: string,
        private _processTemplate?: string,
        private _projectVisibility?: string,
        private _featureIds?: string[]
    ) {
        super("createProjectStoreChanged");
        this._state = {
            navigationUrl: "",
            projectCreationMetadata: {
                canUserCreateProject: undefined as boolean | undefined,
                defaultVersionControlType: {} as IProjectCreationMetadataItemDescriptor,
                projectVisibilityOptions: [] as IProjectCreationMetadataItemDescriptor[],
                defaultProcessTemplate: {} as IProcessTemplateDescriptor,
                isReportingConfigured: false
            },
            existingProjectNames: {},
            featureIds: this._featureIds,
            projectNameState: {
                name: "",
                status: {
                    statusType: StatusType.InputValidationStatus,
                    value: StatusValueType.NoStatus,
                    message: ""
                }
            },
            projectParamsState: {
                projectDescription: "",
                projectVisibilityOptions: [] as IOptionItem[],
                currentProcessTemplateId: "",
                currentProcessTemplateName: "",
                currentVersionControlType: "",
                currentProjectVisibilityOption: {} as IProjectCreationMetadataItemDescriptor
            },
            projectLimitState: {
                isBelowLimit: false,
                isBelowWarning: false,
                status: {
                    statusType: StatusType.ProjectLimitStatus,
                    value: StatusValueType.NoStatus,
                    message: ""
                }
            },
            creationStatus: {
                statusType: StatusType.CreationStatus,
                value: StatusValueType.NoStatus,
                message: "",
                detailedMessage: "",
                creationOperationId: ""
            },
            pageStatus: {
                statusType: StatusType.PageStatus,
                value: StatusValueType.NoStatus,
                message: ""
            },
            source: this._source,
            showAdvancedOptions: false,
            statusMessage: {
                message: "",
                statusMessageType: StatusMessageType.NoStatus,
                showTryAgainCta: false,
                showGoBackCta: false
            }
        };
    }

    public get state(): ICreateProjectComponentState {
        return this._state;
    }

    public onProjectCreationMetadataLoadStatusChanged = (payload: IProjectCreationMetadataLoadedPayload): void => {
        // Update the status
        this._state.pageStatus.value = payload.pageStatus.value;
        this._state.pageStatus.message = payload.pageStatus.message;
        this._state.statusMessage = payload.statusMessage;

        if (payload.pageStatus.value === StatusValueType.Success) {
            // Set the required metadata
            if (payload.projectCreationMetadata) {
                this._state.projectCreationMetadata = payload.projectCreationMetadata;
                this._state.projectParamsState.currentProcessTemplateId = payload.projectCreationMetadata.defaultProcessTemplate.typeId;
                this._state.projectParamsState.currentProcessTemplateName = payload.projectCreationMetadata.defaultProcessTemplate.name;
                this._state.projectParamsState.currentVersionControlType = payload.projectCreationMetadata.defaultVersionControlType.id;

                if (!this._featureIds && payload.projectCreationMetadata.defaultFeatureIds) {
                    this._state.featureIds = payload.projectCreationMetadata.defaultFeatureIds;
                }

                if (!this._projectVisibility && payload.projectCreationMetadata.defaultVisibility) {
                    this._projectVisibility = payload.projectCreationMetadata.defaultVisibility;
                }
            }

            if (this._versionControlType || this._processTemplate) {
                this._state.showAdvancedOptions = true;

                if (this._versionControlType) {
                    this._state.projectParamsState.currentVersionControlType = this._versionControlType;
                }

                if (this._processTemplate) {
                    this._state.projectParamsState.currentProcessTemplateId = "";
                    this._state.projectParamsState.currentProcessTemplateName = this._processTemplate;
                }
            }

            // Updates the state to hold the values for the VC types and Process Templates
            this._populateProjectVisibilityOptions();
        }

        this.emitChanged();
    };

    public updateExistingProjectNames = (payload: string[]): void => {
        payload.forEach(
            (projectName: string): void => {
                this.state.existingProjectNames[projectName.toLowerCase()] = true;
            }
        );
        this.emitChanged();
    };

    public onProjectCreationStatusChanged = (payload: IProjectCreationStatusPayload): void => {
        this._state.navigationUrl = payload.navigationUrl;
        this._state.statusMessage = payload.statusMessage;
        this._state.creationStatus.value = payload.creationStatus.value;
        this._state.creationStatus.message = payload.creationStatus.message;
        this._state.creationStatus.detailedMessage = payload.creationStatus.detailedMessage;
        this._state.creationStatus.creationOperationId = payload.projectCreationOperationId;

        this.emitChanged();
    };

    public onProjectNameValidationStatusChanged = (payload: IProjectNameValidationStatusPayload): void => {
        this._state.projectNameState.name = payload.projectName;
        const status = this._state.projectNameState.status;
        if (this._state.projectNameState.name) {
            status.value = payload.status.value;
            status.message = payload.status.message;
        } else {
            status.value = StatusValueType.NoStatus;
            status.message = "";
        }

        // Removing the status since there has been a retry in the project name text box
        this._state.creationStatus.value = StatusValueType.NoStatus;
        this._state.creationStatus.message = "";
        this._state.projectNameState.status = status;

        this.emitChanged();
    };

    public onProjectLimitCheckStatusChanged = (payload: IProjectLimitCheckStatusPayload): void => {
        this._state.projectLimitState.isBelowLimit = payload.isBelowLimit;
        this._state.projectLimitState.isBelowWarning = payload.isBelowWarning;
        this._state.projectLimitState.status.value = payload.status.value;
        this._state.projectLimitState.status.message = payload.status.message;

        this.emitChanged();
    };

    public onProjectDescriptionChanged = (value: string): void => {
        this._state.projectParamsState.projectDescription = value;
        this.emitChanged();
    };

    public onVersionControlTypeChanged = (selectedType: string): void => {
        this._state.projectParamsState.currentVersionControlType = selectedType;
        this.emitChanged();
    };

    public onProjectVisibilityOptionChanged = (selectedIndex: number): void => {
        this._state.projectParamsState.currentProjectVisibilityOption = this._state.projectCreationMetadata.projectVisibilityOptions[selectedIndex];
        this.emitChanged();
    };

    public onProcessTemplateChanged = (selectedTemplateTypeId: string): void => {
        this._state.projectParamsState.currentProcessTemplateId = selectedTemplateTypeId;
        this.emitChanged();
    };

    public onStatusMessageDismissed = (payload: void): void => {
        this._state.creationStatus.value = StatusValueType.NoStatus;
        this._state.creationStatus.message = "";

        this._state.pageStatus.value = StatusValueType.NoStatus;
        this._state.pageStatus.message = "";

        this._hideStatusMessage();

        this.emitChanged();
    };

    public onAdvancedOptionVisibilityToggle = (payload: void): void => {
        this._state.showAdvancedOptions = !this._state.showAdvancedOptions;

        this.emitChanged();
    };

    private _hideStatusMessage(): void {
        this._state.statusMessage.message = "";
        this._state.statusMessage.statusMessageType = StatusMessageType.NoStatus;
        this._state.statusMessage.showTryAgainCta = false;
    }

    /**
     * Get the project visibility options from metadata and populate the state
     */
    private _populateProjectVisibilityOptions(): void {
        const projectVisibilityOptionItems: IOptionItem[] = [];
        const projectVisibilityOptions = this._state.projectCreationMetadata.projectVisibilityOptions;

        if (projectVisibilityOptions && projectVisibilityOptions.length > 0) {
            let initialVisibility: IProjectCreationMetadataItemDescriptor | null = null;
            let defaultVisibility = projectVisibilityOptions[0];
            projectVisibilityOptions.forEach((value: IProjectCreationMetadataItemDescriptor) => {
                projectVisibilityOptionItems.push({
                    text: value.name
                });
                if (this._projectVisibility && value.id === this._projectVisibility && !value.isDisabled) {
                    initialVisibility = value;
                } else if (value.isDefault) {
                    defaultVisibility = value;
                }
            });

            this._state.projectParamsState.currentProjectVisibilityOption = initialVisibility ? initialVisibility : defaultVisibility;
        }

        if (projectVisibilityOptionItems.length < 2) {
            // Don't show visibility options if choice options are less than two.
            this._state.projectParamsState.projectVisibilityOptions = [];
        } else {
            this._state.projectParamsState.projectVisibilityOptions = projectVisibilityOptionItems;
        }
    }
}
